import { useCallback, useMemo, useSyncExternalStore } from 'react';

import {
	endRemix,
	getRemixMachine,
	isAnyRemixingNow,
	sendRemixEvent,
	startRemix,
	subscribeToAnyMachine,
} from '@atlassian/malleable-ui-components/state/machine';
import {
	holdRemixHandoff,
	REMIX_HANDOFF_HOLD_SEND_START_GRACE_MS,
	type RemixHandoffHoldReleaseReason,
} from '@atlassian/malleable-ui-components/state/machine/remix-handoff-hold';
import type {
	EditorRemixLifecycleAdapter,
	EditorRemixLifecycleEndReason,
	EditorRemixLifecycleEvent,
	EditorRemixLifecycleHandle,
	EditorRemixLifecyclePhase,
	EditorRemixLifecycleStartOutcome,
	EditorRemixLifecycleStartRequest,
} from '@atlassian/malleable-ui-components/types';
import {
	admitAndRetainEditorRemixLifecycle,
	EditorRemixLifecycleOwnerProvider,
	useEditorRemixLifecycle,
	useEditorRemixLifecycleOwner,
	type EditorRemixLifecycleOwner,
	type EditorRemixLifecycleRead,
	type EditorRemixLifecycleRetentionOutcome,
	type RetainedEditorRemixLifecycle,
} from '@atlassian/malleable-ui-components/editor-remix/lifecycle-owner';
import { deriveMachineEditorRemixPhase } from '@atlassian/malleable-ui-components/editor-remix/lifecycle';

import {
	isExperimentEnabled,
	UNSAFE_isExperimentEnabledNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { preloadRemixWatcherHost } from './RemixWatcherMount.tsx';
import {
	useObjectSidebarRemixLifecycleDiagnostics,
	type ObjectSidebarRemixLifecycleOperation,
} from './useObjectSidebarRemixLifecycleDiagnostics.ts';
import { useObjectSidebarRemixParityDiagnostics } from './useObjectSidebarRemixParityDiagnostics.ts';
import type {
	ObjectSidebarRemixEntryPath as SharedObjectSidebarRemixEntryPath,
	ObjectSidebarRemixPhase,
} from './objectSidebarRemixParity.ts';

export type ObjectSidebarRemixEntryPath = SharedObjectSidebarRemixEntryPath;

export type ObjectSidebarRemixLifecycleAdapter = Omit<
	EditorRemixLifecycleAdapter,
	'observeLegacyPhase'
> & {
	bindObservationToken?: (remixId: string, token: symbol) => void;
	observeLegacyPhase?: (
		token: symbol | undefined,
		phase: ObjectSidebarRemixPhase,
		entryPath?: ObjectSidebarRemixEntryPath,
	) => void;
};
export type ObjectSidebarRemixLifecycleEndReason = EditorRemixLifecycleEndReason;
export type ObjectSidebarRemixLifecycleEvent = EditorRemixLifecycleEvent;
export type ObjectSidebarRemixLifecycleHandle = EditorRemixLifecycleHandle;
export type ObjectSidebarRemixLifecycleOwner = EditorRemixLifecycleOwner;
export type ObjectSidebarRemixLifecyclePhase = ObjectSidebarRemixPhase;
export type ObjectSidebarRemixLifecycleRead = EditorRemixLifecycleRead;
export type ObjectSidebarRemixLifecycleRetentionOutcome = EditorRemixLifecycleRetentionOutcome;
export type ObjectSidebarRemixLifecycleStartOutcome = EditorRemixLifecycleStartOutcome;
export type ObjectSidebarRemixLifecycleStartRequest = EditorRemixLifecycleStartRequest;
export type RetainedObjectSidebarRemixLifecycle = RetainedEditorRemixLifecycle;

/** Capture the terminal machine value before INSERTED schedules machine destruction. */
export const projectObjectSidebarMachinePhaseAfterEvent = (
	eventType: EditorRemixLifecycleEvent['type'],
	phase: EditorRemixLifecyclePhase | 'done' | undefined,
): ObjectSidebarRemixPhase | EditorRemixLifecyclePhase => {
	if (eventType === 'INSERTED' && phase === 'done') {
		return 'inserted';
	}
	return deriveMachineEditorRemixPhase(
		phase as Parameters<typeof deriveMachineEditorRemixPhase>[0],
	);
};

export const admitAndRetainObjectSidebarRemixLifecycle: typeof admitAndRetainEditorRemixLifecycle =
	admitAndRetainEditorRemixLifecycle;
export const ObjectSidebarRemixLifecycleOwnerProvider: typeof EditorRemixLifecycleOwnerProvider =
	EditorRemixLifecycleOwnerProvider;
export const useObjectSidebarRemixLifecycle: typeof useEditorRemixLifecycle =
	useEditorRemixLifecycle;
export const useObjectSidebarRemixLifecycleOwner: typeof useEditorRemixLifecycleOwner =
	useEditorRemixLifecycleOwner;

const OBJECT_SIDEBAR_REMIX_LIFECYCLE_EXPERIMENT = 'remix_state_rearchitecture_object_sidebar';

/**
 * Whether the Object Sidebar Remix lifecycle treatment is on for this session.
 *
 * Reads without exposure: exposure is recorded exactly once at admission in
 * `useObjectSidebarRemixLifecycleAdapter.startFlow`.
 */
export const isObjectSidebarRemixLifecycleEnabled = (): boolean =>
	fg('remix_state_rearchitecture_enabled') &&
	UNSAFE_isExperimentEnabledNoExposure(OBJECT_SIDEBAR_REMIX_LIFECYCLE_EXPERIMENT);

const subscribeToNothing = () => () => {};
const readNotBlocked = () => false;

/**
 * Whether any Remix flow is active in the shared machine, so the Object Sidebar should not offer
 * a new start. Picker visibility is not machine activity, so an open picker never counts.
 *
 * Control never subscribes and always reads `false`, which leaves its established behaviour alone.
 */
export const useIsObjectSidebarRemixStartBlocked = (isLifecycleTreatment: boolean): boolean =>
	useSyncExternalStore(
		isLifecycleTreatment ? subscribeToAnyMachine : subscribeToNothing,
		isLifecycleTreatment ? isAnyRemixingNow : readNotBlocked,
		isLifecycleTreatment ? isAnyRemixingNow : readNotBlocked,
	);

export const createObjectSidebarRemixLifecycleHandle = ({
	conversationId,
	logDuplicateTerminalAttempt,
	logLifecycleOperationError,
	reportParityObserverFailure,
	reportHandoffHoldReleased,
	releaseParityFlow,
	remixId,
}: {
	conversationId: string;
	logDuplicateTerminalAttempt: (reason: EditorRemixLifecycleEndReason) => void;
	logLifecycleOperationError: (
		operation: ObjectSidebarRemixLifecycleOperation,
		error: unknown,
	) => void;
	reportParityObserverFailure: (operation: 'releaseParityFlow') => void;
	reportHandoffHoldReleased: (reason: RemixHandoffHoldReleaseReason) => void;
	releaseParityFlow: (
		remixId: string,
		capturedMachinePhase?: EditorRemixLifecyclePhase | ObjectSidebarRemixLifecyclePhase,
	) => void;
	remixId: string;
}): EditorRemixLifecycleHandle => {
	let hasSettled = false;
	const safelyReportParityObserverFailure = (): void => {
		try {
			reportParityObserverFailure('releaseParityFlow');
		} catch {
			// A parity observer or its diagnostics must never affect lifecycle behavior.
		}
	};

	const end: EditorRemixLifecycleHandle['end'] = (reason) => {
		if (hasSettled) {
			logDuplicateTerminalAttempt(reason);
			return;
		}
		hasSettled = true;
		try {
			releaseParityFlow(remixId);
		} catch {
			safelyReportParityObserverFailure();
		}
		if (reason === 'handed-off') {
			// Rovo Chat keeps generating after the handoff, so hold "a Remix is in progress" until its
			// conversation settles. Hold first, so other entry points never see a gap before the end.
			try {
				// Still thinking means the handoff came right after the send, before any result, so the
				// conversation may be slow to turn busy. Past thinking, the result already arrived, so an
				// idle conversation means the generation is done (CCCEMAU-3768).
				const phase = getRemixMachine(remixId)?.state();
				const isResultPending = phase === 'thinking';
				holdRemixHandoff({
					conversationId,
					source: 'object-sidebar',
					...(isResultPending && { startGraceMs: REMIX_HANDOFF_HOLD_SEND_START_GRACE_MS }),
					hasStartedGenerating: phase !== undefined && !isResultPending,
					onRelease: reportHandoffHoldReleased,
				});
			} catch (error) {
				// Part of ending the flow, so report it with the end.
				logLifecycleOperationError('endRemix', error);
			}
		}
		try {
			endRemix(remixId, reason);
		} catch (error) {
			logLifecycleOperationError('endRemix', error);
		}
	};

	const transition: EditorRemixLifecycleHandle['transition'] = (event) => {
		if (hasSettled) {
			logDuplicateTerminalAttempt(event.type === 'INSERTED' ? 'abandoned' : 'cancelled');
			return false;
		}
		try {
			const machine = getRemixMachine(remixId);
			const phaseBefore = machine?.state();
			sendRemixEvent(remixId, event);
			const phaseAfter = machine?.state();
			const accepted = phaseBefore !== phaseAfter;
			if (accepted && event.type === 'INSERTED') {
				hasSettled = true;
				// The machine's real terminal phase is `done`, which the shared editor mapper
				// intentionally projects to inactive because it is normally destroyed immediately.
				// Preserve the INSERTED terminal evidence before that teardown.
				const capturedPhase = projectObjectSidebarMachinePhaseAfterEvent(event.type, phaseAfter);
				try {
					releaseParityFlow(remixId, capturedPhase);
				} catch {
					safelyReportParityObserverFailure();
				}
			}
			return accepted;
		} catch (error) {
			logLifecycleOperationError('sendTransition', error);
			return false;
		}
	};

	const rollback: EditorRemixLifecycleHandle['rollback'] = () => {
		if (hasSettled) {
			logDuplicateTerminalAttempt('cancelled');
			return;
		}
		try {
			sendRemixEvent(remixId, { type: 'CANCELLED' });
		} catch (error) {
			logLifecycleOperationError('sendCancelled', error);
		} finally {
			end('cancelled');
		}
	};

	return { conversationId, remixId, rollback, transition, end };
};

export const useObjectSidebarRemixLifecycleAdapter = (): ObjectSidebarRemixLifecycleAdapter => {
	const parity = useObjectSidebarRemixParityDiagnostics();
	const { observeFlow, releaseFlow, recordLegacyPhase, bindObservationToken } = parity;
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const reportParityObserverFailure = useCallback(
		(operation: ObjectSidebarRemixLifecycleOperation): void => {
			try {
				reportLifecycleDiagnostic('failed', { operation });
			} catch {
				// Observer and analytics failures must never affect the Remix lifecycle.
			}
		},
		[reportLifecycleDiagnostic],
	);
	const safelyObserveFlow = useCallback(
		(remixId: string): void => {
			try {
				observeFlow(remixId);
			} catch {
				reportParityObserverFailure('observeParityFlow');
				// A parity observer must never affect lifecycle admission.
			}
		},
		[observeFlow, reportParityObserverFailure],
	);
	const safelyReleaseFlow = useCallback(
		(
			remixId: string,
			capturedMachinePhase?: EditorRemixLifecyclePhase | ObjectSidebarRemixLifecyclePhase,
		): void => {
			try {
				releaseFlow(
					remixId,
					capturedMachinePhase === 'inserting' ? 'previewing' : capturedMachinePhase,
				);
			} catch {
				reportParityObserverFailure('releaseParityFlow');
				// A parity observer must never affect lifecycle teardown.
			}
		},
		[releaseFlow, reportParityObserverFailure],
	);
	const safelyRecordLegacyPhase = useCallback(
		(
			token: symbol | undefined,
			phase: ObjectSidebarRemixLifecyclePhase,
			entryPath?: ObjectSidebarRemixEntryPath,
		): void => {
			try {
				recordLegacyPhase(token, phase, entryPath);
			} catch {
				reportParityObserverFailure('recordLegacyParityPhase');
				// A parity observer must never affect the legacy flow.
			}
		},
		[recordLegacyPhase, reportParityObserverFailure],
	);
	const safelyBindObservationToken = useCallback(
		(remixId: string, token: symbol): void => {
			try {
				bindObservationToken(remixId, token);
			} catch {
				reportParityObserverFailure('bindParityObservationToken');
				// A parity observer must never affect lifecycle retention.
			}
		},
		[bindObservationToken, reportParityObserverFailure],
	);
	const logLifecycleOperationError = useCallback(
		(operation: ObjectSidebarRemixLifecycleOperation, error: unknown): void => {
			reportLifecycleDiagnostic('failed', {
				operation,
				errorName: error instanceof Error ? error.name : typeof error,
			});
		},
		[reportLifecycleDiagnostic],
	);
	const logDuplicateTerminalAttempt = useCallback(
		(reason: EditorRemixLifecycleEndReason): void => {
			reportLifecycleDiagnostic('observed', {
				operation: 'duplicateTerminalAttempt',
				terminalReason: reason,
			});
		},
		[reportLifecycleDiagnostic],
	);
	const reportHandoffHoldReleased = useCallback(
		(reason: RemixHandoffHoldReleaseReason): void => {
			reportLifecycleDiagnostic('observed', {
				operation: 'handoffHoldReleased',
				holdReleaseReason: reason,
			});
		},
		[reportLifecycleDiagnostic],
	);
	const startFlow = useCallback(
		(request: EditorRemixLifecycleStartRequest): EditorRemixLifecycleStartOutcome => {
			if (!fg('remix_state_rearchitecture_enabled')) {
				return { status: 'disabled' };
			}

			if (!isExperimentEnabled(OBJECT_SIDEBAR_REMIX_LIFECYCLE_EXPERIMENT)) {
				return { status: 'disabled' };
			}

			preloadRemixWatcherHost();
			try {
				const result = startRemix({ ...request, metadata: {} });
				if (!result) {
					return { status: 'active-flow-rejected' };
				}

				const handle = createObjectSidebarRemixLifecycleHandle({
					...result,
					logDuplicateTerminalAttempt,
					logLifecycleOperationError,
					reportParityObserverFailure,
					reportHandoffHoldReleased,
					releaseParityFlow: safelyReleaseFlow,
				});
				if (
					request.conversationId !== undefined &&
					result.conversationId !== request.conversationId
				) {
					logLifecycleOperationError(
						'validateAdmissionIdentity',
						new Error('Machine admission returned a different conversation identity'),
					);
					handle.rollback();
					return { status: 'shadow-infrastructure-failure' };
				}
				safelyObserveFlow(result.remixId);

				return {
					status: 'admitted',
					handle,
				};
			} catch (error) {
				logLifecycleOperationError('startRemix', error);
				return { status: 'shadow-infrastructure-failure' };
			}
		},
		[
			logDuplicateTerminalAttempt,
			logLifecycleOperationError,
			reportHandoffHoldReleased,
			reportParityObserverFailure,
			safelyObserveFlow,
			safelyReleaseFlow,
		],
	);

	return useMemo(
		() => ({
			startFlow,
			bindObservationToken: safelyBindObservationToken,
			observeLegacyPhase: safelyRecordLegacyPhase,
		}),
		[safelyBindObservationToken, safelyRecordLegacyPhase, startFlow],
	);
};
